// src/hooks/useStudyAlgorithm.ts
// =====================================================================
// ALGORITMO DE ALTA PERFORMANCE v3.2
//
// CORREÇÃO v3.2 (modo Custom Study):
//   Adicionado suporte a isCustomStudy — pool de múltiplos subdecks
//   salvo no localStorage. O hook rotaciona entre os pares
//   deckId/subdeckId do pool, usando o mesmo algoritmo de urgência.
//
// CORREÇÃO CRÍTICA (bug duplo clique):
//   O preloadNextCard agora recebe o histórico como PARÂMETRO,
//   garantindo que o card atual sempre está no ?recent= antes do preload.
//
// ORDEM DE DECLARAÇÃO CORRIGIDA v3.1:
//   loadSavedPool → loadNextSavedCard → loadNextCard → handleAnswer
// =====================================================================

import { useState, useRef, useCallback, useEffect } from 'react';

// =====================================================================
// INTERFACES
// =====================================================================

export interface CardData {
    card: {
        _id: string;
        front: string;
        back: string;
        supportContent?: string;
        references?: string;
        deckId: string;
        subdeckId: string;
        media?: {
            images?: Array<{ url: string; side: string }>;
            audios?: Array<{ url: string; side: string }>;
        };
    };
    progress: {
        masteryLevel: number;
        priorityScore: number;
        status: string;
        lastRating: number | null;
        repetitions: number;
        totalReviews: number;
        ratingHistory?: number[];
    };
    urgencyScore?: number;
    reason?: string;
}

export interface CustomStudyPoolItem {
    cycleId: string;
    deckId: string;
    subdeckId: string;
    isCustomDeck?: boolean;
}

export interface AlgorithmConfig {
    deckId: string;
    subdeckId: string;
    isCustomDeck?: boolean;
    isSavedMode?: boolean;
    isReviewMode?: boolean;
    isCustomStudy?: boolean;          // ✅ NOVO: modo estudo personalizado
    customPool?: CustomStudyPoolItem[]; // ✅ NOVO: pool de pares deckId/subdeckId
    isUserContent?: boolean;          // ✅ NOVO: conteúdo criado pelo usuário
    onBeforeLoadNextCard?: () => void;
    onComplete?: () => void;
}

// =====================================================================
// HELPER PURO (fora do hook): buscar próximo card do servidor
// Recebe o histórico já atualizado como parâmetro — zero closures stale.
// =====================================================================

async function fetchNextCard(
    deckId: string,
    subdeckId: string,
    recentHistory: string[],
    isCustomDeck: boolean,
    token: string,
    isUserContent = false
): Promise<CardData | null> {
    const recentParam = recentHistory.slice(-15).join(',');
    const baseUrl = isUserContent
        ? `/api/user-content/study/${deckId}/next`
        : isCustomDeck
            ? `/api/progress/custom-next/${deckId}/${subdeckId}`
            : `/api/progress/next/${deckId}/${subdeckId}`;
    const url = `${baseUrl}${recentParam ? `?recent=${recentParam}` : ''}`;

    console.log('📡 Buscando próximo card:', url);

    const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
    });

    if (!response.ok) {
        console.error('❌ Erro ao buscar card:', response.status);
        return null;
    }

    const data = await response.json();
    console.log('✅ Card recebido:', data.card._id);
    return data;
}

// =====================================================================
// HOOK PRINCIPAL
// =====================================================================

export function useStudyAlgorithm(config: AlgorithmConfig) {
    const {
        deckId,
        subdeckId,
        isCustomDeck = false,
        isSavedMode = false,
        isReviewMode = false,
        isCustomStudy = false,
        isUserContent = false,
        onBeforeLoadNextCard,
    } = config;

    // ===== ESTADOS =====
    const [currentCard, setCurrentCard] = useState<CardData | null>(null);
    const [cardStack, setCardStack] = useState<CardData[]>([]);
    const [stackIndex, setStackIndex] = useState(-1);

    // Histórico como ref — síncrona, nunca stale, sem re-render desnecessário
    const cardHistoryRef = useRef<string[]>([]);

    const [loading, setLoading] = useState(true);

    // Buffer: próximo card pré-carregado
    const nextCardBufferRef = useRef<CardData | null>(null);
    const [hasBufferedCard, setHasBufferedCard] = useState(false);

    const isPreloadingRef = useRef(false);
    const [isAnswering, setIsAnswering] = useState(false);

    // ===== REVIEW MODE =====
    const [reviewCardIds, setReviewCardIds] = useState<string[]>([]);
    const [initialReviewCount, setInitialReviewCount] = useState(0);
    const [loadingReviewIds, setLoadingReviewIds] = useState(false);

    // ===== SAVED MODE (refs para evitar stale closures) =====
    const savedCardPoolRef = useRef<any[]>([]);
    const savedPoolLoadedRef = useRef(false);

    // ===== CUSTOM STUDY MODE =====
    // Pool de pares deckId/subdeckId lido do localStorage
    const customStudyPoolRef = useRef<CustomStudyPoolItem[]>([]);

    // ===== GUARDS =====
    const isLoadingCard = useRef(false);
    const hasInitialized = useRef(false);

    // =====================================================================
    // PRÉ-CARREGAR PRÓXIMO CARD
    // =====================================================================

    const preloadNextCard = useCallback(async (historyWithCurrentCard: string[]) => {
        if (isPreloadingRef.current) return;
        isPreloadingRef.current = true;

        try {
            const token = localStorage.getItem('accessToken') || '';
            let data: CardData | null = null;

            if (isSavedMode) {
                const recentParam = historyWithCurrentCard.slice(-15).join(',');
                const url = `/api/progress/saved-next${recentParam ? `?recent=${recentParam}` : ''}`;
                const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
                if (response.ok) data = await response.json();
            } else if (isCustomStudy) {
                // ✅ Pré-carregar do pool custom study
                data = await fetchNextCustomStudyCard(
                    customStudyPoolRef.current,
                    historyWithCurrentCard,
                    token
                );
            } else {
                data = await fetchNextCard(deckId, subdeckId, historyWithCurrentCard, isCustomDeck, token, isUserContent);
            }

            if (data) {
                console.log('✅ Próximo card pré-carregado:', data.card._id);
                nextCardBufferRef.current = data;
                setHasBufferedCard(true);
            }
        } catch (error) {
            console.error('❌ Erro ao pré-carregar:', error);
        } finally {
            isPreloadingRef.current = false;
        }
    }, [deckId, subdeckId, isSavedMode, isCustomDeck, isCustomStudy, isUserContent]);

    // =====================================================================
    // CARREGAR PRÓXIMO CARD DO POOL CUSTOM STUDY
    // Rotaciona aleatoriamente entre os pares deckId/subdeckId do pool
    // =====================================================================

    const fetchNextCustomStudyCard = async (
        pool: CustomStudyPoolItem[],
        recentHistory: string[],
        token: string
    ): Promise<CardData | null> => {
        if (pool.length === 0) return null;

        const recentParam = recentHistory.slice(-15).join(',');
        const shuffled = [...pool].sort(() => Math.random() - 0.5);

        for (const item of shuffled) {
            // ← MODIFICADO: usar endpoint correto baseado em isCustomDeck
            const baseUrl = (item as any).isCustomDeck
                ? `/api/progress/custom-next/${item.deckId}/${item.subdeckId}`
                : `/api/progress/next/${item.deckId}/${item.subdeckId}`;

            const url = `${baseUrl}${recentParam ? `?recent=${recentParam}` : ''}`;

            try {
                const response = await fetch(url, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.ok) {
                    const data = await response.json();
                    data.card.deckId = data.card.deckId || item.deckId;
                    data.card.subdeckId = data.card.subdeckId || item.subdeckId;
                    data.card.isCustomDeck = (item as any).isCustomDeck ?? false;
                    console.log(`✅ [CUSTOM STUDY] Card recebido: ${data.card._id} (deck: ${item.deckId}, isCustom: ${(item as any).isCustomDeck})`);
                    return data;
                }
            } catch (err) {
                console.error(`❌ [CUSTOM STUDY] Erro no par ${item.deckId}/${item.subdeckId}:`, err);
            }
        }

        return null;
    };

    // =====================================================================
    // CARREGAR POOL DE CARDS SALVOS
    // =====================================================================

    const loadSavedPool = useCallback(async (): Promise<any[]> => {
        if (savedPoolLoadedRef.current && savedCardPoolRef.current.length > 0) {
            return savedCardPoolRef.current;
        }

        const token = localStorage.getItem('accessToken') || '';
        const response = await fetch('/api/saved-cards/study-mode', {
            headers: { Authorization: `Bearer ${token}` }
        });

        if (!response.ok) return [];

        const data = await response.json();
        const pool = data.cards || [];
        savedCardPoolRef.current = pool;
        savedPoolLoadedRef.current = true;
        console.log(`✅ [SAVED] Pool carregado: ${pool.length} cards`);
        return pool;
    }, []);

    // =====================================================================
    // SELECIONAR PRÓXIMO CARD DO POOL DE SALVOS
    // =====================================================================

    const loadNextSavedCard = useCallback(async (
        pool: any[],
        recentCards: string[]
    ): Promise<CardData | null> => {
        if (pool.length === 0) return null;

        const token = localStorage.getItem('accessToken') || '';
        const deckGroups = new Map<string, { deckId: string; subdeckId: string }>();

        for (const card of pool) {
            const key = `${card.deckId}::${card.subdeckId}`;
            if (!deckGroups.has(key)) {
                deckGroups.set(key, { deckId: card.deckId, subdeckId: card.subdeckId });
            }
        }

        const groups = Array.from(deckGroups.values()).sort(() => Math.random() - 0.5);

        for (const group of groups) {
            const recentParam = recentCards.join(',');
            const url = `/api/progress/next/${group.deckId}/${group.subdeckId}${recentParam ? `?recent=${recentParam}` : ''}`;

            try {
                const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
                if (response.ok) {
                    const data = await response.json();
                    const cardIdStr = data.card._id.toString();
                    const isInPool = pool.some(p =>
                        p._id === cardIdStr || p._id?.toString() === cardIdStr
                    );
                    if (isInPool) {
                        data.card.deckId = data.card.deckId || group.deckId;
                        data.card.subdeckId = data.card.subdeckId || group.subdeckId;
                        return data;
                    }
                }
            } catch (err) {
                console.error(`❌ [SAVED] Erro no grupo ${group.deckId}:`, err);
            }
        }

        // Fallback
        try {
            const recentParam = recentCards.join(',');
            const url = `/api/progress/saved-next${recentParam ? `?recent=${recentParam}` : ''}`;
            const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
            if (response.ok) return await response.json();
        } catch (err) {
            console.error('❌ [SAVED] Fallback falhou:', err);
        }

        return null;
    }, []);

    // =====================================================================
    // CARREGAR PRÓXIMO CARD (modo normal, saved, review ou customStudy)
    // =====================================================================

    const loadNextCard = useCallback(async () => {
        if (isLoadingCard.current) return;
        isLoadingCard.current = true;
        setLoading(true);
        onBeforeLoadNextCard?.();

        try {
            const token = localStorage.getItem('accessToken') || '';

            // ─── REVIEW MODE ─────────────────────────────────────────
            if (isReviewMode) {
                const recentParam = cardHistoryRef.current.slice(-15).join(',');
                const reviewUrl = isUserContent
                    ? `/api/user-content/study/${deckId}/review-next`
                    : isCustomDeck
                        ? `/api/progress/custom-review-next/${deckId}/${subdeckId}`
                        : `/api/progress/review-next/${deckId}/${subdeckId}`;
                const url = `${reviewUrl}${recentParam ? `?recent=${recentParam}` : ''}`;

                const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
                if (response.ok) {
                    const data = await response.json();
                    const newHistory = [...cardHistoryRef.current, data.card._id];
                    cardHistoryRef.current = newHistory;
                    setCurrentCard(data);
                    setCardStack(prev => [...prev, data]);
                    setStackIndex(prev => prev + 1);
                    preloadNextCard(newHistory);
                }
                return;
            }

            // ─── SAVED MODE ──────────────────────────────────────────
            if (isSavedMode) {
                const pool = await loadSavedPool();
                if (pool.length === 0) return;

                const data = await loadNextSavedCard(pool, cardHistoryRef.current.slice(-15));
                if (data) {
                    const newHistory = [...cardHistoryRef.current, data.card._id];
                    cardHistoryRef.current = newHistory;
                    setCurrentCard(data);
                    setCardStack(prev => [...prev, data]);
                    setStackIndex(prev => prev + 1);
                    preloadNextCard(newHistory);
                }
                return;
            }

            // ─── CUSTOM STUDY MODE ───────────────────────────────────
            if (isCustomStudy) {
                // Tentar usar buffer primeiro
                const buffered = nextCardBufferRef.current;
                if (buffered) {
                    console.log('⚡ [CUSTOM STUDY] Usando card pré-carregado:', buffered.card._id);
                    nextCardBufferRef.current = null;
                    setHasBufferedCard(false);

                    const newHistory = [...cardHistoryRef.current, buffered.card._id];
                    cardHistoryRef.current = newHistory;
                    setCurrentCard(buffered);
                    setCardStack(prev => [...prev, buffered]);
                    setStackIndex(prev => prev + 1);
                    preloadNextCard(newHistory);
                    return;
                }

                // Buscar direto
                const data = await fetchNextCustomStudyCard(
                    customStudyPoolRef.current,
                    cardHistoryRef.current,
                    token
                );
                if (data) {
                    const newHistory = [...cardHistoryRef.current, data.card._id];
                    cardHistoryRef.current = newHistory;
                    setCurrentCard(data);
                    setCardStack(prev => [...prev, data]);
                    setStackIndex(prev => prev + 1);
                    preloadNextCard(newHistory);
                }
                return;
            }

            // ─── MODO NORMAL ─────────────────────────────────────────
            const buffered = nextCardBufferRef.current;
            if (buffered) {
                console.log('⚡ Usando card pré-carregado:', buffered.card._id);

                nextCardBufferRef.current = null;
                setHasBufferedCard(false);

                const newHistory = [...cardHistoryRef.current, buffered.card._id];
                cardHistoryRef.current = newHistory;

                setCurrentCard(buffered);
                setCardStack(prev => [...prev, buffered]);
                setStackIndex(prev => prev + 1);

                preloadNextCard(newHistory);
                return;
            }

            // Sem buffer: busca diretamente
            console.log('⚠️ Buffer vazio — buscando direto...');
            const data = await fetchNextCard(deckId, subdeckId, cardHistoryRef.current, isCustomDeck, token, isUserContent);

            if (data) {
                const newHistory = [...cardHistoryRef.current, data.card._id];
                cardHistoryRef.current = newHistory;
                setCurrentCard(data);
                setCardStack(prev => [...prev, data]);
                setStackIndex(prev => prev + 1);
                preloadNextCard(newHistory);
            }

        } catch (error) {
            console.error('❌ Erro ao carregar próximo card:', error);
        } finally {
            setLoading(false);
            isLoadingCard.current = false;
        }
    }, [
        isReviewMode, isSavedMode, isCustomDeck, isCustomStudy, isUserContent,
        deckId, subdeckId,
        onBeforeLoadNextCard,
        preloadNextCard,
        loadSavedPool,
        loadNextSavedCard,
    ]);

    // =====================================================================
    // CARREGAR PRIMEIRO CARD (sem histórico)
    // =====================================================================

    const loadFirstCard = useCallback(async () => {
        if (isLoadingCard.current) return;
        isLoadingCard.current = true;
        setLoading(true);

        try {
            const token = localStorage.getItem('accessToken') || '';

            if (isSavedMode) {
                const pool = await loadSavedPool();
                if (pool.length === 0) return;

                const data = await loadNextSavedCard(pool, []);
                if (data) {
                    cardHistoryRef.current = [data.card._id];
                    setCurrentCard(data);
                    setCardStack([data]);
                    setStackIndex(0);
                    preloadNextCard([data.card._id]);
                }
                return;
            }

            // ✅ CUSTOM STUDY MODE
            if (isCustomStudy) {
                console.log('🎯 [CUSTOM STUDY] Carregando primeiro card do pool...');
                const data = await fetchNextCustomStudyCard(
                    customStudyPoolRef.current,
                    [],
                    token
                );
                if (data) {
                    cardHistoryRef.current = [data.card._id];
                    setCurrentCard(data);
                    setCardStack([data]);
                    setStackIndex(0);
                    preloadNextCard([data.card._id]);
                }
                return;
            }

            const data = await fetchNextCard(deckId, subdeckId, [], isCustomDeck, token, isUserContent);

            if (data) {
                cardHistoryRef.current = [data.card._id];
                setCurrentCard(data);
                setCardStack([data]);
                setStackIndex(0);
                preloadNextCard([data.card._id]);
            }
        } catch (error) {
            console.error('❌ Erro ao carregar primeiro card:', error);
        } finally {
            setLoading(false);
            isLoadingCard.current = false;
        }
    }, [deckId, subdeckId, isCustomDeck, isSavedMode, isCustomStudy, isUserContent, preloadNextCard, loadSavedPool, loadNextSavedCard]);

    // =====================================================================
    // CARREGAR REVIEW CARDS
    // =====================================================================

    const loadReviewCards = useCallback(async () => {
        try {
            setLoadingReviewIds(true);
            const token = localStorage.getItem('accessToken') || '';
            const reviewCardsUrl = isUserContent
                ? `/api/user-content/study/${deckId}/review-cards`
                : isCustomDeck
                    ? `/api/progress/custom-review-cards/${deckId}/${subdeckId}`
                    : `/api/progress/review-cards/${deckId}/${subdeckId}`;

            const response = await fetch(reviewCardsUrl, { headers: { Authorization: `Bearer ${token}` } });

            if (response.ok) {
                const data = await response.json();
                setReviewCardIds(data.reviewCardIds || []);
                setInitialReviewCount(data.count || 0);

                if (data.count > 0) {
                    loadFirstCard();
                } else {
                    setLoading(false);
                }
            } else {
                setLoading(false);
            }
        } catch (error) {
            console.error('❌ [REVIEW] Erro:', error);
            setLoading(false);
        } finally {
            setLoadingReviewIds(false);
        }
    }, [deckId, subdeckId, isCustomDeck, isUserContent, loadFirstCard]);

    // =====================================================================
    // INICIALIZAÇÃO
    // =====================================================================

    useEffect(() => {
        if (hasInitialized.current) return;

        // ✅ CORRIGIDO: isCustomStudy/isUserContent não precisam de subdeckId
        if (!isSavedMode && !isCustomStudy && !isUserContent && (!deckId || !subdeckId)) {
            console.error('❌ [INIT] deckId ou subdeckId ausente');
            setLoading(false);
            return;
        }
        if (!isSavedMode && !isCustomStudy && isUserContent && !deckId) {
            console.error('❌ [INIT] deckId ausente para user content');
            setLoading(false);
            return;
        }

        // ✅ Carregar pool do localStorage para custom study
        if (isCustomStudy) {
            const poolRaw = localStorage.getItem('customStudyPool');
            if (!poolRaw) {
                console.error('❌ [CUSTOM STUDY] Pool não encontrado no localStorage');
                setLoading(false);
                return;
            }
            try {
                const pool = JSON.parse(poolRaw) as CustomStudyPoolItem[];
                if (pool.length === 0) {
                    console.error('❌ [CUSTOM STUDY] Pool vazio');
                    setLoading(false);
                    return;
                }
                customStudyPoolRef.current = pool;
                console.log(`✅ [CUSTOM STUDY] Pool carregado: ${pool.length} subdecks`);
            } catch (e) {
                console.error('❌ [CUSTOM STUDY] Erro ao parsear pool:', e);
                setLoading(false);
                return;
            }
        }

        console.log('\n🚀 [INIT] Inicializando algoritmo...');
        console.log(`   DeckId: ${deckId} | SubdeckId: ${subdeckId}`);
        console.log(`   Modo: ${isReviewMode ? 'Review' : isSavedMode ? 'Salvos' : isCustomStudy ? 'Custom Study' : 'Normal'}`);

        hasInitialized.current = true;

        if (isReviewMode) {
            loadReviewCards();
        } else {
            loadFirstCard();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // =====================================================================
    // PROCESSAR RESPOSTA
    // =====================================================================

    const handleAnswer = useCallback(async (rating: number, onSuccess?: () => void) => {
        if (!currentCard || isAnswering) {
            console.log('⏸️ handleAnswer bloqueado');
            return;
        }

        setIsAnswering(true);

        const cardId = currentCard.card._id;
        const cardDeckId = currentCard.card.deckId || deckId;
        const cardSubdeckId = currentCard.card.subdeckId || subdeckId;
        const token = localStorage.getItem('accessToken') || '';

        const cardIsCustomDeck = (currentCard.card as any).isCustomDeck ?? isCustomDeck;
        const answerUrl = cardIsCustomDeck
            ? '/api/progress/custom-answer'
            : '/api/progress/answer';

        console.log(`\n🎯 OPTIMISTIC ANSWER: card=${cardId} rating=${rating}`);

        // ─── CAMINHO RÁPIDO: buffer disponível ──────────────────────
        const buffered = nextCardBufferRef.current;
        if (buffered) {
            nextCardBufferRef.current = null;
            setHasBufferedCard(false);

            const newHistory = [...cardHistoryRef.current, buffered.card._id];
            cardHistoryRef.current = newHistory;

            setCurrentCard(buffered);
            setCardStack(prev => [...prev, buffered]);
            setStackIndex(prev => prev + 1);

            setIsAnswering(false);

            if (onSuccess) await onSuccess();

            preloadNextCard(newHistory);

            fetch(answerUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ deckId: cardDeckId, subdeckId: cardSubdeckId, cardId, rating })
            })
                .then(res => {
                    if (res.ok) console.log(`✅ [BG] Resposta registrada: card=${cardId} rating=${rating}`);
                    else console.error(`❌ [BG] Erro:`, res.status);
                })
                .catch(err => console.error('❌ [BG] Falha:', err));

            return;
        }

        // ─── CAMINHO FALLBACK: sem buffer ────────────────────────────
        console.log('⚠️ [FALLBACK] Buffer vazio — aguardando servidor...');

        try {
            const response = await fetch(answerUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ deckId: cardDeckId, subdeckId: cardSubdeckId, cardId, rating })
            });

            if (response.ok) {
                console.log(`✅ Resposta registrada: card=${cardId} rating=${rating}`);
                if (onSuccess) await onSuccess();
                setIsAnswering(false);
                await loadNextCard();
            } else {
                console.error('❌ Erro na resposta:', response.status);
                setIsAnswering(false);
            }
        } catch (error) {
            console.error('❌ Erro ao registrar resposta:', error);
            setIsAnswering(false);
        }
    }, [
        currentCard, isAnswering, isCustomDeck,
        deckId, subdeckId,
        preloadNextCard, loadNextCard,
    ]);

    // =====================================================================
    // VOLTAR PARA CARD ANTERIOR
    // =====================================================================

    const handleGoBack = useCallback(() => {
        if (stackIndex > 0) {
            const previousIndex = stackIndex - 1;
            setStackIndex(previousIndex);
            setCurrentCard(cardStack[previousIndex]);
        }
    }, [stackIndex, cardStack]);

    // =====================================================================
    // PULAR CARD
    // =====================================================================

    const handleSkip = useCallback(async () => {
        if (!isLoadingCard.current) {
            await loadNextCard();
        }
    }, [loadNextCard]);

    // =====================================================================
    // RETORNO DO HOOK
    // =====================================================================

    return {
        currentCard,
        loading,
        cardHistory: cardHistoryRef.current,
        stackIndex,
        canGoBack: stackIndex > 0,

        reviewCardIds,
        initialReviewCount,
        loadingReviewIds,

        loadNextCard,
        loadReviewCards,
        handleAnswer,
        handleGoBack,
        handleSkip,
        preloadNextCard,

        isLoadingCard: isLoadingCard.current,
        isAnswering,
        hasBufferedCard,
    };
}